import React, { createContext, useState, useContext, useEffect } from 'react'
import supabase from '../lib/supabase'

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [needsProfile, setNeedsProfile] = useState(false)

  useEffect(() => {
    let mounted = true

    const initializeAuth = async () => {
      try {
        console.log('Initializing auth...')
        
        // Get initial session with timeout
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Session timeout')), 10000)
        )
        
        const { data: { session }, error: sessionError } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ])
        
        if (!mounted) return
        
        if (sessionError) {
          console.error('Session error:', sessionError)
          setError(sessionError.message)
          setUser(null)
          setLoading(false)
          return
        }
        
        if (session?.user) {
          console.log('Session found:', session.user.email)
          setUser(session.user)
          setNeedsProfile(false) // Simplify for now
        } else {
          console.log('No session found')
          setUser(null)
          setNeedsProfile(false)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        if (mounted) {
          setError(error.message)
          setUser(null)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event)
        if (!mounted) return
        
        if (session?.user) {
          setUser(session.user)
          setNeedsProfile(false)
        } else {
          setUser(null)
          setNeedsProfile(false)
        }
        setLoading(false)
      }
    )

    return () => {
      mounted = false
      subscription?.unsubscribe()
    }
  }, [])

  const signUp = async ({ email, password, username }) => {
    try {
      setError(null)
      setLoading(true)
      
      // Check if username is already taken
      const { data: existingUsers, error: usernameError } = await supabase
        .from('profiles_sb2847x9')
        .select('id')
        .eq('username', username);
      
      if (usernameError) throw usernameError;
      
      if (existingUsers && existingUsers.length > 0) {
        throw new Error('This username is already taken. Please choose a different one.');
      }
      
      console.log('Signing up user:', email)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { username: username }
        }
      })

      if (authError) {
        console.error('Auth error during signup:', authError)
        throw authError
      }

      if (!authData.user) {
        console.error('No user returned from signup')
        throw new Error('Failed to create account')
      }
      
      console.log('User signed up successfully:', authData.user.email)
      
      // Create user profile
      const { error: profileError } = await supabase
        .from('profiles_sb2847x9')
        .insert([{
          id: authData.user.id,
          username: username,
          bio: '',
          avatar_icon: 'FiUser',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);
      
      if (profileError) {
        console.error('Error creating profile:', profileError);
        if (profileError.code === '23505') { // Unique constraint violation
          throw new Error('This username is already taken. Please choose a different one.');
        } else {
          throw profileError;
        }
      }
      
      setUser(authData.user)
      return { user: authData.user, session: authData.session }
    } catch (error) {
      console.error('Error during sign up:', error)
      setError(error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signIn = async ({ email, password }) => {
    try {
      console.log('Signing in user:', email)
      setError(null)
      setLoading(true)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) {
        console.error('Sign in error:', error)
        throw error
      }

      console.log('User signed in successfully:', data.user?.email)
      setUser(data.user)
      return data
    } catch (error) {
      console.error('Error signing in:', error)
      setError(error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      console.log('Signing out user')
      setLoading(true)
      
      const { error } = await supabase.auth.signOut()
      
      if (error) {
        console.error('Sign out error:', error)
        throw error
      }

      console.log('User signed out successfully')
      setUser(null)
      setNeedsProfile(false)
    } catch (error) {
      console.error('Error signing out:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  const value = {
    user,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    needsProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}